import React, { useEffect, useState } from 'react';
import { Cloud, Loader2 } from 'lucide-react';
import { formatBytes } from '../../components/utils/time';
import { RUNTIME } from '../../config/runtime';
import { getDriveStatus, previewDriveSelection, type DriveConnectionStatus } from '../../services/drive';

interface Props {
  departmentId: string;
  requiredBytes: number;
  category?: string;
  isRTL: boolean;
  value?: string;
  onChange: (connectorId: string | undefined) => void;
}

export const DriveDestinationSelector: React.FC<Props> = ({ departmentId, requiredBytes, category, isRTL, value, onChange }) => {
  const [status, setStatus] = useState<DriveConnectionStatus | null>(null);
  const [preview, setPreview] = useState<{ id: string; label: string; availableBytes: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const canChoose = status?.policy?.mode === 'manual' || status?.policy?.allowUserOverride;

  useEffect(() => {
    if (!RUNTIME.isCloudConfigured || !departmentId) return;
    let alive = true;
    setLoading(true);
    void getDriveStatus(departmentId).then(next => { if (alive) setStatus(next); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [departmentId]);

  useEffect(() => {
    if (!RUNTIME.isCloudConfigured || !departmentId || !status) return;
    let alive = true;
    setLoading(true);
    void previewDriveSelection({ departmentId, bytes: requiredBytes, category, connectorId: value })
      .then(result => {
        if (!alive || !result.connector) return;
        setPreview({ id: result.connector.id, label: result.connector.displayName || result.connector.email || result.connector.id, availableBytes: result.connector.availableBytes });
        if (canChoose && !value) onChange(result.connector.id);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [canChoose, category, departmentId, onChange, requiredBytes, status, value]);

  if (!RUNTIME.isCloudConfigured) return null;
  const eligible = (status?.connectors ?? []).filter(item => item.status === 'connected' && (item.availableBytes ?? Number.MAX_SAFE_INTEGER) >= requiredBytes);
  return (
    <section className="drive-destination" aria-labelledby="drive-destination-title">
      <div className="flex items-center gap-2"><Cloud size={16} aria-hidden="true" /><h4 id="drive-destination-title" className="text-xs font-bold">{isRTL ? 'وجهة التخزين' : 'Storage destination'}</h4>{loading && <Loader2 size={14} className="animate-spin" aria-label={isRTL ? 'جارٍ التحقق' : 'Checking'} />}</div>
      {canChoose && eligible.length > 1 ? (
        <label className="mt-2 block"><span className="sr-only">{isRTL ? 'اختر حساب Drive' : 'Choose Drive account'}</span><select className="form-control" value={value ?? ''} onChange={event => onChange(event.target.value || undefined)}>{eligible.map(item => <option key={item.id} value={item.id}>{item.displayName || item.email || item.id} · {formatBytes(item.availableBytes ?? 0)}</option>)}</select></label>
      ) : (
        <p className="mt-2 text-xs text-champagne-400">{preview ? <>{isRTL ? 'سيُحفظ في' : 'Will be stored in'} <strong>{preview.label}</strong> · <span dir="ltr">{formatBytes(preview.availableBytes)}</span></> : (isRTL ? 'لا توجد وجهة متاحة بعد.' : 'No destination is currently available.')}</p>
      )}
    </section>
  );
};
